import type { ChainCheckpoint, ContactSummary, CredibilityTier } from '../types/contact'

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

export interface AdminStatus {
  total_users: number
  total_reports_24h: number
  total_contacts: number
  contacts_by_status: Record<string, number>
}

export interface AdminUser {
  id: string
  username: string
  email: string
  role: string
  credibility_tier: CredibilityTier
  trust_multiplier: number
  professional_request_status: 'none' | 'pending' | 'approved' | 'rejected'
  is_active: boolean
  blocked_reason: string | null
  created_at: string | null
  last_login_at: string | null
}

export interface ProfessionalRequest {
  id: string
  username: string
  email: string
  professional_details: string | null
  professional_request_status: string
  created_at: string | null
}

export interface FlaggedReport {
  id: string
  species_bucket: string
  event_type: string
  description: string | null
  reporter_tier: string
  flag_count: number
  flag_reason: string | null
  status: string
  reported_at: string
  contact_id: string | null
}

async function unwrap<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { detail?: string }).detail ?? fallback)
  }
  return res.json()
}

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` }
}

export async function fetchAdminStatus(token: string): Promise<AdminStatus> {
  const res = await fetch(`${BASE}/admin/status`, { headers: authHeaders(token) })
  return unwrap<AdminStatus>(res, 'Failed to load platform status')
}

export interface UserSearchFilters {
  q?: string
  credibility_tier?: string
  role?: string
}

export async function fetchAdminUsers(
  token: string,
  filters: UserSearchFilters = {},
): Promise<AdminUser[]> {
  const params = new URLSearchParams()
  if (filters.q) params.set('q', filters.q)
  if (filters.credibility_tier) params.set('credibility_tier', filters.credibility_tier)
  if (filters.role) params.set('role', filters.role)
  const res = await fetch(`${BASE}/admin/users?${params.toString()}`, { headers: authHeaders(token) })
  return unwrap<AdminUser[]>(res, 'Failed to load users')
}

export async function updateUserTier(
  token: string,
  userId: string,
  credibilityTier: string,
): Promise<{ id: string; credibility_tier: string }> {
  const res = await fetch(`${BASE}/admin/users/${userId}/tier`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ credibility_tier: credibilityTier }),
  })
  return unwrap(res, 'Failed to update tier')
}

export async function updateUserTrust(
  token: string,
  userId: string,
  trustMultiplier: number,
): Promise<{ id: string; trust_multiplier: number }> {
  const res = await fetch(`${BASE}/admin/users/${userId}/trust`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ trust_multiplier: trustMultiplier }),
  })
  return unwrap(res, 'Failed to update trust multiplier')
}

export async function blockUser(
  token: string,
  userId: string,
  reason: string,
): Promise<{ id: string; is_active: boolean; reports_reject_requested: number }> {
  const res = await fetch(`${BASE}/admin/users/${userId}/block`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ reason: reason || null }),
  })
  return unwrap(res, 'Failed to block user')
}

export async function unblockUser(token: string, userId: string): Promise<{ id: string; is_active: boolean }> {
  const res = await fetch(`${BASE}/admin/users/${userId}/unblock`, {
    method: 'POST',
    headers: authHeaders(token),
  })
  return unwrap(res, 'Failed to unblock user')
}

export async function fetchProfessionalRequests(token: string): Promise<ProfessionalRequest[]> {
  const res = await fetch(`${BASE}/admin/users/professional-requests`, { headers: authHeaders(token) })
  return unwrap<ProfessionalRequest[]>(res, 'Failed to load professional-tier requests')
}

export async function rejectProfessionalRequest(token: string, userId: string): Promise<void> {
  const res = await fetch(`${BASE}/admin/users/${userId}/professional-request/reject`, {
    method: 'POST',
    headers: authHeaders(token),
  })
  await unwrap(res, 'Failed to reject professional-tier request')
}

export async function fetchFlaggedReports(token: string): Promise<FlaggedReport[]> {
  const res = await fetch(`${BASE}/admin/reports/flagged`, { headers: authHeaders(token) })
  return unwrap<FlaggedReport[]>(res, 'Failed to load flagged reports')
}

export async function rejectReport(token: string, reportId: string): Promise<void> {
  const res = await fetch(`${BASE}/admin/reports/${reportId}/reject`, {
    method: 'POST',
    headers: authHeaders(token),
  })
  await unwrap(res, 'Failed to reject report')
}

export interface AdminContactFilters {
  status?: string
  voided_only?: boolean
}

export async function fetchAdminContacts(
  token: string,
  filters: AdminContactFilters = {},
): Promise<ContactSummary[]> {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.voided_only) params.set('voided_only', 'true')
  const res = await fetch(`${BASE}/admin/contacts?${params.toString()}`, { headers: authHeaders(token) })
  return unwrap<ContactSummary[]>(res, 'Failed to load contacts')
}

export async function forceVoidContact(
  token: string,
  contactId: string,
  reason: string,
): Promise<{ id: string; status: string }> {
  const res = await fetch(`${BASE}/admin/contacts/${contactId}/void`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ reason: reason || null }),
  })
  return unwrap(res, 'Failed to void contact')
}

export async function createCheckpoint(token: string): Promise<ChainCheckpoint> {
  const res = await fetch(`${BASE}/ledger/checkpoints`, { method: 'POST', headers: authHeaders(token) })
  return unwrap<ChainCheckpoint>(res, 'Failed to create checkpoint')
}

export async function stampCheckpointAnchor(token: string, checkpointId: string): Promise<void> {
  const res = await fetch(`${BASE}/ledger/checkpoints/${checkpointId}/anchor`, {
    method: 'POST',
    headers: authHeaders(token),
  })
  await unwrap(res, 'Failed to stamp checkpoint anchor')
}

export async function upgradeCheckpointAnchor(token: string, checkpointId: string): Promise<void> {
  const res = await fetch(`${BASE}/ledger/checkpoints/${checkpointId}/anchor/upgrade`, {
    method: 'POST',
    headers: authHeaders(token),
  })
  await unwrap(res, 'Failed to upgrade checkpoint anchor')
}
